/**
 * Normalised view of a failed backend call.
 *
 * Both backends answer errors as JSON, but in different shapes:
 * - MIM REST API and Identity Director Service: `{ error: { code, message, target? } }`
 *   with codes such as `resource-not-found`, `permission-denied`, `bad-request`.
 * - older Service builds: `{ status: 'error', message }` (the code is derived from the HTTP status).
 * Network failures and proxies produce anything else (HTML, plain text, nothing).
 */
export interface ApiError {
  code: string | null;
  message: string | null;
  status: number | null;
}

const STATUS_CODES: {[status: number]: string} = {
  0: 'server-error',
  400: 'bad-request',
  401: 'unauthorized',
  403: 'permission-denied',
  404: 'resource-not-found',
  500: 'internal-server-error',
  504: 'gateway-timeout',
};

const CODE_PATTERN: RegExp = /^[a-z]+(-[a-z]+)*$/;

function codeForStatus(status: number | null): string | null {
  return status != null && STATUS_CODES[status] != null ? STATUS_CODES[status] : null;
}

export function getApiError(error: any): ApiError {
  if (error == null) {
    return {code: null, message: null, status: null};
  }
  const status: number | null = typeof error.status === 'number' ? error.status : null;
  // HttpErrorResponse carries the body in `error`; a bare body may also be passed in directly
  const body: any = typeof error === 'object' && 'error' in error ? error.error : error;

  if (body != null && typeof body === 'object') {
    if (body.error != null && typeof body.error === 'object') {
      return {
        code: typeof body.error.code === 'string' ? body.error.code : codeForStatus(status),
        message: typeof body.error.message === 'string' ? body.error.message : null,
        status: status,
      };
    }
    if (typeof body.code === 'string') {
      return {
        code: body.code,
        message: typeof body.message === 'string' ? body.message : null,
        status: status,
      };
    }
    if (typeof body.message === 'string') {
      return {code: codeForStatus(status), message: body.message, status: status};
    }
  }
  if (typeof body === 'string' && CODE_PATTERN.test(body)) {
    return {code: body, message: null, status: status};
  }
  return {
    code: codeForStatus(status) ?? (typeof error.code === 'string' ? error.code : null),
    message: typeof error.message === 'string' ? error.message : null,
    status: status,
  };
}

export function getApiErrorCode(error: any): string | null {
  return getApiError(error).code;
}

export function getApiErrorMessage(error: any): string | null {
  return getApiError(error).message;
}
